/**
 * What the operator is told when a batch stops.
 *
 * This card is the last chance to be honest. It is a SectionCard, not a toast,
 * it is toned by the WORST outcome, and it must never contain success copy
 * while anything failed or was orphaned. It also carries the only record of
 * un-deletable storage objects: nothing can list that bucket, so if this list is
 * lost the objects are lost.
 */
import React from 'react'
import { Link } from 'react-router'
import { brand } from '../../theme/brand'
import { Pill, SectionCard, type Tone } from '../../theme/listkit'
import { thresholdWarning } from './helpers'
import type { BatchOutcome } from './uploadQueue'

export const SUMMARY_COPY = {
  orphans: 'Uploaded files with no database row (needs manual cleanup):',
  ids: 'Photo ids created in this batch:',
  link: 'View in Photo Gallery',
  /** EC-34: the max(created_at) clamp query failed, so ordering was not verified. */
  orderUnverified:
    'The latest existing photo in this album could not be read, so the position of this batch relative to it was not verified.',
} as const

/** EC-55: ONE aggregated line per batch, not one report per failed file. */
export function failureDigest(outcome: BatchOutcome, totalRows: number): string | null {
  if (outcome.failed === 0) return null
  const distinct = Array.from(new Set(outcome.errorCodes)).slice(0, 5)
  const codes = distinct.length > 0 ? ` Codes: ${distinct.join(', ')}.` : ''
  return `${outcome.failed} of ${totalRows} failed.${codes}`
}

export function summaryTone(outcome: BatchOutcome): Tone {
  if (outcome.failed > 0 || outcome.orphanPaths.length > 0) return 'red'
  if (outcome.cancelled > 0) return 'neutral'
  if (outcome.skipped > 0 || outcome.asIsCount > 0) return 'gold'
  return 'green'
}

export function summaryHeadline(outcome: BatchOutcome, albumName: string): string {
  if (outcome.failed > 0) return `Finished with ${outcome.failed} failures.`
  if (outcome.cancelled > 0) {
    return `Cancelled — ${outcome.done} photos were already published to "${albumName}".`
  }
  // summaryTone already returns red for an orphan, so without this the pill read
  // "Finished: 12 uploaded." in red — a clean-run sentence over an object nobody
  // can list. EC-20 forbids reporting a clean run while one exists.
  if (outcome.orphanPaths.length > 0) {
    const n = outcome.orphanPaths.length
    return n === 1
      ? `Finished: ${outcome.done} uploaded, 1 file needs manual cleanup.`
      : `Finished: ${outcome.done} uploaded, ${n} files need manual cleanup.`
  }
  return `Finished: ${outcome.done} uploaded.`
}

/**
 * The shape of Refine's useInvalidate() return value, narrowed to what this
 * module uses. Typed here rather than cast at the call site: `invalidate as never`
 * would compile and then hide a genuine signature change.
 */
export type Invalidate = (args: {
  resource: string
  /** Mirrors `keyof IQueryKeys` (contexts/data/types.d.mts:178-190). */
  invalidates: Array<'all' | 'resourceAll' | 'list' | 'many' | 'detail' | 'logList'>
}) => void | Promise<void>

/**
 * `void p` on a real promise attaches NO handler, and Refine's invalidate() is
 * async - it awaits Promise.all(...) and calls pickDataProvider, which THROWS
 * for an unknown provider. That throw would surface as an unhandledrejection
 * with nothing to attribute it to. The page is explicit about the same rule at
 * its one other fire-and-forget call site ("this is the one `void ... .then()`
 * on the page, so it gets the catch").
 */
const fire = (p: void | Promise<void>): void => {
  void Promise.resolve(p).catch(() => undefined)
}

/** Refine caches lists for 5 minutes with liveMode 'off'; without this the operator re-uploads. */
export function runInvalidations(invalidate: Invalidate, opts: { albumCreated: boolean }): void {
  fire(invalidate({ resource: 'photos', invalidates: ['list', 'many'] }))
  fire(invalidate({ resource: 'admin_photos_summary', invalidates: ['list'] }))
  if (opts.albumCreated) fire(invalidate({ resource: 'albums', invalidates: ['list', 'many'] }))
}

/**
 * EC-71: one photo this batch created, as the operator needs it to undo the
 * batch by hand. The id alone is not enough - the per-photo DeleteButton removes
 * the `photos` row and leaves the storage object behind, and nothing can list
 * that bucket to find it afterwards - so the path travels with it.
 */
export interface CreatedPhoto {
  id: string
  /** BulkRow.path, i.e. the object this photo's url points at. */
  path?: string
}

export interface BulkUploadSummaryProps {
  outcome: BatchOutcome
  albumName: string
  totalRows: number
  albumCreatedEmpty: boolean
  postBatchTotal: number | null
  /** False when the max(created_at) clamp query failed (EC-34). */
  orderVerified: boolean
  /**
   * The done rows' ids paired with their storage paths (EC-71). Optional: the
   * outcome's own `photoIds` is the fallback, and it renders ids alone - which
   * is what a caller that has no rows to hand can honestly show.
   */
  photoRecords?: ReadonlyArray<CreatedPhoto>
}

const mono: React.CSSProperties = {
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
  fontSize: 11.5,
  color: brand.ink,
  background: brand.paper,
  border: '1px solid ' + brand.cardLine,
  borderRadius: 8,
  padding: 10,
  whiteSpace: 'pre-wrap',
  overflowWrap: 'anywhere',
  userSelect: 'text',
}

const line: React.CSSProperties = { fontFamily: brand.fSans, fontSize: 12.5, color: brand.sub }

export function BulkUploadSummary({
  outcome,
  albumName,
  totalRows,
  albumCreatedEmpty,
  postBatchTotal,
  orderVerified,
  photoRecords,
}: BulkUploadSummaryProps) {
  const rowCap = postBatchTotal == null ? null : thresholdWarning(postBatchTotal)
  const megabytes = (outcome.uploadedBytes / (1024 * 1024)).toFixed(1)
  const digest = failureDigest(outcome, totalRows)
  // One line per created photo: "<id>  <path>", or just the id when the caller
  // could not supply a path. Rendered as ONE pre-wrap text node, like the orphan
  // block, so 200 rows cost 200 lines and not 200 elements.
  const created: ReadonlyArray<CreatedPhoto> =
    photoRecords && photoRecords.length > 0 ? photoRecords : outcome.photoIds.map((id) => ({ id }))
  const createdText = created.map((r) => (r.path ? `${r.id}  ${r.path}` : r.id)).join('\n')

  return (
    <div data-testid="muaa-bulk-upload-summary">
      <SectionCard title="Summary">
        <div style={{ display: 'grid', gap: 10 }}>
          <Pill tone={summaryTone(outcome)}>{summaryHeadline(outcome, albumName)}</Pill>

          {digest ? (
            <div data-testid="muaa-bulk-upload-failure-digest" style={line}>
              {digest}
            </div>
          ) : null}

          {!orderVerified ? <div style={line}>{SUMMARY_COPY.orderUnverified}</div> : null}

          {outcome.skipped > 0 ? (
            <div style={line}>{outcome.skipped} skipped as duplicates of files in this batch.</div>
          ) : null}

          {outcome.asIsCount > 0 ? (
            <div style={line}>
              {outcome.asIsCount} of {totalRows} uploaded without resizing; their embedded metadata was not
              removed.
            </div>
          ) : null}

          <div style={line}>{megabytes} MB uploaded.</div>

          {rowCap ? <div style={line}>{rowCap}</div> : null}

          {albumCreatedEmpty ? (
            <div style={line}>
              Album "{albumName}" was created and is empty — delete it from Albums if it was a mistake.
            </div>
          ) : null}

          {outcome.orphanPaths.length > 0 ? (
            <div style={{ display: 'grid', gap: 6 }}>
              <div style={line}>{SUMMARY_COPY.orphans}</div>
              <div data-testid="muaa-bulk-upload-orphans" style={mono}>
                {outcome.orphanPaths.join('\n')}
              </div>
            </div>
          ) : null}

          {created.length > 0 ? (
            <div style={{ display: 'grid', gap: 6 }}>
              <div style={line}>{SUMMARY_COPY.ids}</div>
              <div data-testid="muaa-bulk-upload-photo-ids" style={mono}>
                {createdText}
              </div>
            </div>
          ) : null}

          <Link to="/photos">{SUMMARY_COPY.link}</Link>
        </div>
      </SectionCard>
    </div>
  )
}
